import {
  isParameterChange,
  isSystemChangeRequest,
  ParameterChange,
  SystemChangeRequest,
  BaseEvent,
  useAffiliateEvent
} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useRequiredUser} from '@hconnect/login'
import {ShellFooter, PageContainer, Shell} from '@hconnect/uikit/src/lib2'
import {BaseErrorPage} from '@hconnect/uikit/src/lib2/pages/BaseErrorPage'
import {useUpmPlant, useUpmPlantTimezone} from '@hconnect/upm'
import {usePermission} from '@hproduce/permissions'
import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router'

import {ReactComponent as NotAvailableIllustration} from '../assets/notAvailable.svg'
import {EventAffiliatesHeader, EventContent} from '../components'
import {FEATURE_NAMESPACE} from '../consts'

const isApproverEvent = (event: BaseEvent): event is ParameterChange | SystemChangeRequest =>
  isParameterChange(event) || isSystemChangeRequest(event)
const isValidEventId = (eventId: string): boolean => /^e_\d+$/.test(eventId)

export const EventsAffiliatesPage: FC = () => {
  const {plantId, eventId} = useParams<{plantId: string; eventId: string}>()
  if (!plantId) throw new Error('plantId is required')
  if (!eventId) throw new Error('eventId is required')
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const user = useRequiredUser()
  if (!user) throw new Error('User is required')
  const timezone = useUpmPlantTimezone(plantId)
  const {country} = useUpmPlant({plantId})
  const hasPermission = usePermission({
    plantContext: {plantId, countryId: country},
    permissionTypes: ['EDIT_PLANT_EVENTS_AFFILIATE']
  })

  const {
    data: event,
    isLoading,
    isError,
    error
  } = useAffiliateEvent(
    {plantId, timezone, eventId},
    {enabled: hasPermission && isValidEventId(eventId)}
  )

  const isAccessDenied =
    !isValidEventId(eventId) ||
    (event &&
      (!isApproverEvent(event) ||
        !event.approvers?.some((approver) => approver.user.id === user.id)))

  if (
    !hasPermission ||
    isAccessDenied ||
    error?.response?.status === 401 ||
    error?.response?.status === 403
  ) {
    return (
      <Shell
        header={<EventAffiliatesHeader plantId={plantId} />}
        isResponsive
        footer={<ShellFooter label={t('shiftEventsAffiliates.label.poweredBy')} />}
      >
        <BaseErrorPage
          illustration={<NotAvailableIllustration />}
          caption={t('shiftEventsAffiliates.error.eventAccessDenied')}
          dataTestId="event-access-denied-error"
        />
      </Shell>
    )
  }

  return (
    <Shell
      header={<EventAffiliatesHeader plantId={plantId} />}
      isResponsive
      footer={<ShellFooter label={t('shiftEventsAffiliates.label.poweredBy')} />}
    >
      <DataContentWrapper showLoader={isLoading} isError={isError} data={event}>
        {event && (
          <PageContainer>
            <EventContent event={event} plantId={plantId} />
          </PageContainer>
        )}
      </DataContentWrapper>
    </Shell>
  )
}
